/**
 * Heap analytics helpers.
 *
 * Heap is injected via an async loader stub (see `pages/_app.tsx`) only for
 * SMB-driver brands and never in `dev`. In production the failing events
 * (Sentry cluster COO-WEBAPP-300/301/302/303/304/305/2X2/2Y3) show that
 * `window.heap.track` can EXIST yet throw `heap.push is not a function` when
 * called — the stub's `track` wrapper delegates to `heap.push`, which is
 * undefined while the real SDK is mid-load (or replaced by a blocker/shim).
 *
 * Therefore "ready" is defined as *a `track` call that actually succeeds*, not
 * merely "track is a function". Every Heap access is guarded and fails silently
 * (no Sentry noise). Events fired before Heap can accept them are buffered and
 * flushed — in order — once a real `track` call goes through, so they are not
 * lost to the load race.
 */

interface PendingEvent {
  eventName: string;
  properties?: Record<string, unknown>;
}

/** Buffer of events fired before Heap could accept them. Bounded by `MAX_QUEUE`. */
const pendingEvents: PendingEvent[] = [];
/** Hard cap on buffered events to bound memory; events beyond this are dropped. */
const MAX_QUEUE = 50;
/** Ensures we only ever schedule a single flush watcher at a time. */
let flushScheduled = false;

/** Poll interval (ms) for the flush watcher. */
const FLUSH_INTERVAL_MS = 250;
/** Max flush attempts before giving up (~5s total) so the timer never leaks. */
const MAX_FLUSH_ATTEMPTS = 20;

/**
 * Attempt a single `heap.track` call.
 * @returns true only if Heap accepted the event (present, callable, no throw).
 * Returns false when Heap is absent, `track` is not callable, OR the call throws
 * (e.g. the `heap.push is not a function` stub race) — the caller should buffer.
 */
const tryTrack = (eventName: string, properties?: Record<string, unknown>): boolean => {
  if (typeof window === 'undefined' || typeof window.heap?.track !== 'function') {
    return false;
  }

  try {
    window.heap.track(eventName, properties);
    return true;
  } catch {
    // Silent: never let analytics break checkout, never report to Sentry.
    return false;
  }
};

/**
 * Drain buffered events into Heap, oldest first, stopping at the first event
 * Heap can't yet accept. Only events actually delivered are removed, so a
 * partially-ready Heap never discards undelivered events.
 */
const flushPendingEvents = (): void => {
  while (pendingEvents.length > 0) {
    const next = pendingEvents[0];
    if (tryTrack(next.eventName, next.properties)) {
      pendingEvents.shift();
    } else {
      break;
    }
  }
};

/**
 * Schedule a bounded watcher that flushes the queue once Heap can accept events.
 * No-ops server-side and guards against scheduling more than one watcher.
 */
const scheduleFlush = (): void => {
  if (typeof window === 'undefined' || flushScheduled) {
    return;
  }

  flushScheduled = true;
  let attempts = 0;

  const interval = setInterval(() => {
    attempts += 1;
    flushPendingEvents();

    if (pendingEvents.length === 0) {
      clearInterval(interval);
      flushScheduled = false;
    } else if (attempts >= MAX_FLUSH_ATTEMPTS) {
      // Give up; drop the buffer so it doesn't grow unbounded across retries.
      clearInterval(interval);
      flushScheduled = false;
      pendingEvents.length = 0;
    }
  }, FLUSH_INTERVAL_MS);
};

/** Buffer an event (respecting the cap) and ensure a flush watcher is running. */
const enqueueEvent = (eventName: string, properties?: Record<string, unknown>): void => {
  if (pendingEvents.length < MAX_QUEUE) {
    pendingEvents.push({ eventName, properties });
  }
  scheduleFlush();
};

/**
 * Safely track events with Heap analytics.
 *
 * Handles Heap being blocked, uninitialized, or still loading (the partial stub
 * whose `track` throws `heap.push is not a function`). Fails silently — never
 * throws, never reports to Sentry. Events that can't be delivered yet are
 * buffered and flushed in order once Heap is genuinely ready.
 *
 * @param eventName - The name of the event to track
 * @param properties - Optional properties to send with the event
 * @returns true if the event was delivered to Heap immediately, false if it was
 *   buffered (or dropped because the buffer is full / Heap never became ready)
 */
export const safeHeapTrack = (eventName: string, properties?: Record<string, unknown>): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Preserve ordering: if a backlog exists, drain what we can first and queue
  // this event behind anything still pending.
  if (pendingEvents.length > 0) {
    flushPendingEvents();
  }

  if (pendingEvents.length === 0 && tryTrack(eventName, properties)) {
    return true;
  }

  enqueueEvent(eventName, properties);
  return false;
};

/**
 * Invoke a named Heap method, guarded and silent. Returns true only if the
 * method is present, callable, and does not throw. Shared by the non-buffered
 * helpers below (identify is meaningful only once a session exists, so unlike
 * tracking these calls are not queued).
 */
const safeInvoke = (
  method:
    | 'identify'
    | 'addUserProperties'
    | 'addEventProperties'
    | 'resetIdentity'
    | 'clearEventProperties',
  ...args: unknown[]
): boolean => {
  if (typeof window === 'undefined' || typeof window.heap?.[method] !== 'function') {
    return false;
  }

  try {
    (window.heap[method] as (...callArgs: unknown[]) => void)(...args);
    return true;
  } catch {
    return false;
  }
};

/**
 * Safely identify a user with Heap. Best-effort and silent — identify is not
 * buffered (it is only meaningful once a session exists).
 *
 * @returns true if the call succeeded, false otherwise
 */
export const safeHeapIdentify = (identity: string): boolean => safeInvoke('identify', identity);

/**
 * Safely invoke an arbitrary Heap method (e.g. `addUserProperties`). Best-effort
 * and silent when Heap or the method is unavailable or throws.
 *
 * @returns true if the call succeeded, false otherwise
 */
export const safeHeapCall = (
  method: 'addUserProperties' | 'addEventProperties' | 'resetIdentity' | 'clearEventProperties',
  ...args: unknown[]
): boolean => safeInvoke(method, ...args);
